/**
 * Wizard — guided CV creation flow.
 *
 * Rendered at route `/wizard`. Walks the user through building a CV from
 * scratch with AI assistance. Writes CV data directly into useCvState via
 * useCvEditing — no separate draft state.
 *
 * Steps: Welcome → (FreeText? | Import?) → Basics → Experience → Education →
 *        Skills → More → Template → PreScoreAts → Reformatage → Done
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, ClipboardPaste } from 'lucide-react';

import { useCvState } from '../../hooks/useCvState';
import { useCvEditing } from '../../hooks/useCvEditing';
import { useAppMode } from '../../hooks/useAppMode';
import { useWizard, WIZARD_STEP_ORDER, type WizardStepKey } from '../../hooks/useWizard';
import type { LayoutTemplate } from '../../types';
import type { CommonInfo } from '../../hooks/useCvState';
import type { CVData } from '../../types';

import { StepWelcome } from './steps/StepWelcome';
import { StepFreeText } from './steps/StepFreeText';
import { StepImport } from './steps/StepImport';
import { StepBasics } from './steps/StepBasics';
import { StepExperience } from './steps/StepExperience';
import { StepEducation } from './steps/StepEducation';
import { StepSkills } from './steps/StepSkills';
import { StepMore } from './steps/StepMore';
import { StepTemplate } from './steps/StepTemplate';
import { StepPreScoreAts } from './steps/StepPreScoreAts';
import { StepReformatage } from './steps/StepReformatage';
import { StepDone } from './steps/StepDone';

function readInitialLayout(): LayoutTemplate {
  try {
    const stored = window.localStorage.getItem('cv_layout_template');
    if (stored === 'ats' || stored === 'moderne' || stored === 'classique' || stored === 'pro') {
      return stored;
    }
  } catch { /* ignore */ }
  return 'classique';
}

export function Wizard() {
  const navigate = useNavigate();
  const location = useLocation();
  const wizard = useWizard();
  const { setMode } = useAppMode();

  // Support ?mode=import: skip welcome, go directly to import step
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('mode') === 'import') {
      wizard.setShowImportStep(true);
      wizard.goTo('import');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cvState = useCvState();
  const { setData, setCommonInfo, setFieldVisibility, data, commonInfo } = cvState;

  const editing = useCvEditing({
    data,
    setData,
    setCommonInfo,
    setFieldVisibility,
  });

  // Layout template choice (persisted to localStorage — picked up by useEditorUi on /app)
  const [activeLayout, setActiveLayout] = useState<LayoutTemplate>(readInitialLayout);

  const handleLayoutChange = useCallback((layout: LayoutTemplate) => {
    setActiveLayout(layout);
    try {
      window.localStorage.setItem('cv_layout_template', layout);
    } catch { /* ignore */ }
  }, []);

  // Blank common-info baseline — so imported data fully replaces any default
  // template values (previous bug: fields missing from the AI parse kept the
  // stock template's firstName/email/phone which looked like "default CV was used").
  const blankCommon = useCallback(
    (): CommonInfo => ({
      firstName: '', lastName: '', civility: '', fullName: '',
      email: '', phone: '', location: '', city: '', country: '',
      birthDate: '', avatar: '', compressedAvatar: '',
      nationality: '', birthPlace: '', gender: '', maritalStatus: '',
    }),
    [],
  );

  // Handle import: overwrite data + commonInfo with parsed result, then advance
  const handleImported = useCallback((parsedData: CVData, parsedCommon: Partial<CommonInfo>) => {
    setData(parsedData);
    setCommonInfo({ ...blankCommon(), ...parsedCommon });
    wizard.goNext();
  }, [setData, setCommonInfo, wizard, blankCommon]);

  // Handle free-text extraction: same as import (overwrites + advances)
  const handleFreeTextExtracted = useCallback((parsedData: CVData, parsedCommon: Partial<CommonInfo>) => {
    setData(parsedData);
    setCommonInfo({ ...blankCommon(), ...parsedCommon });
  }, [setData, setCommonInfo, blankCommon]);

  const handleSkipWizard = useCallback(() => {
    wizard.markSkipped();
    setMode('simple');
    navigate('/app', { replace: true });
  }, [wizard, setMode, navigate]);

  const handleFinish = useCallback((openAdapt = false) => {
    // Create a new named version in localStorage from the wizard's data
    const versionName = (
      data.personalInfo?.title?.trim() ||
      data.experience?.[0]?.position?.trim() ||
      `${commonInfo.firstName} ${commonInfo.lastName}`.trim() ||
      'Mon CV'
    ).slice(0, 60);
    const slug = versionName
      .toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 40) || 'mon-cv';
    const newId = `wizard-${slug}-${Date.now()}`;

    try {
      const rawMeta = localStorage.getItem('cv_templates_meta');
      const meta: Array<{ id: string; name: string }> = rawMeta ? JSON.parse(rawMeta) : [];
      meta.push({ id: newId, name: versionName });
      localStorage.setItem('cv_templates_meta', JSON.stringify(meta));

      const rawData = localStorage.getItem('cv_all_templates_data');
      const allData: Record<string, unknown> = rawData ? JSON.parse(rawData) : {};
      allData[newId] = data;
      localStorage.setItem('cv_all_templates_data', JSON.stringify(allData));

      localStorage.setItem('cv_active_template', newId);
    } catch { /* ignore */ }

    wizard.markDone();
    setMode('simple');
    // openAdapt=true → open the adapt modal immediately in app (Phase 1.5)
    navigate(openAdapt ? '/app?adapt=1' : '/app', { replace: true });
  }, [wizard, setMode, navigate, data, commonInfo]);

  const visibleSteps = useMemo<WizardStepKey[]>(() => {
    return wizard.showImportStep
      ? WIZARD_STEP_ORDER
      : WIZARD_STEP_ORDER.filter((s) => s !== 'import');
  }, [wizard.showImportStep]);

  const isFirstStep = wizard.stepIndex <= 0;
  const isLastStep = wizard.step === 'done';
  const isSkippable = wizard.step === 'more' || wizard.step === 'freetext' || wizard.step === 'prescoreats' || wizard.step === 'reformatage';
  // Welcome has its own card buttons; Done has its own CTA
  const hideFooter = wizard.step === 'welcome' || isLastStep;

  return (
    <div className="min-h-screen w-full bg-[#0a0a0a] text-white flex flex-col">
      {/* Top bar */}
      <header className="shrink-0 border-b border-white/10 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/logo-rectangle-dark.png" alt="ATSmyCV" className="h-7 w-auto" />
          <span className="text-xs font-semibold uppercase tracking-wider text-white/50">
            Création guidée
          </span>
        </div>
        <div className="flex items-center gap-3">
          {/* Show "Import CV" shortcut on all non-import steps in scratch mode */}
          {wizard.step !== 'welcome' && wizard.step !== 'import' && !wizard.showImportStep && (
            <button
              type="button"
              onClick={() => {
                wizard.setShowImportStep(true);
                wizard.goTo('import');
              }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white/60 hover:text-white hover:bg-white/10 border border-white/10 hover:border-white/20 transition-all"
            >
              <ClipboardPaste className="size-3.5" />
              Importer un CV existant
            </button>
          )}
          <button
            type="button"
            onClick={handleSkipWizard}
            className="text-xs text-white/50 hover:text-white/80 transition-colors underline underline-offset-4"
          >
            Non merci, éditeur complet →
          </button>
        </div>
      </header>

      {/* Step indicator */}
      <div className="shrink-0 px-4 py-3 border-b border-white/10 bg-black/20 overflow-x-auto">
        <div className="max-w-3xl mx-auto flex items-center gap-1 min-w-max">
          {visibleSteps.map((key, idx) => {
            const isPast = idx < wizard.stepIndex;
            const isCurrent = idx === wizard.stepIndex;
            return (
              <div key={key} className="flex items-center gap-1">
                <div className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold transition-all whitespace-nowrap ${
                  isCurrent ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/30'
                  : isPast   ? 'bg-emerald-600/20 text-emerald-300'
                  :             'bg-white/5 text-white/40'
                }`}>
                  <span className={`flex items-center justify-center size-4 rounded-full text-[10px] font-bold shrink-0 ${
                    isCurrent ? 'bg-white text-blue-600'
                    : isPast   ? 'bg-emerald-500 text-white'
                    :             'bg-white/10 text-white/40'
                  }`}>
                    {isPast ? <Check className="size-2.5" /> : idx + 1}
                  </span>
                  <span className="hidden sm:inline">{STEP_LABELS[key]}</span>
                </div>
                {idx < visibleSteps.length - 1 && (
                  <div className={`h-0.5 w-4 rounded-full transition-all ${isPast ? 'bg-emerald-500/40' : 'bg-white/5'}`} />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Step content */}
      <main className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-6 py-10">
          {wizard.step === 'welcome' && (
            <StepWelcome setShowImportStep={wizard.setShowImportStep} goNext={wizard.goNext} />
          )}
          {wizard.step === 'freetext' && (
            <StepFreeText
              onExtracted={handleFreeTextExtracted}
              goNext={wizard.goNext}
            />
          )}
          {wizard.step === 'import' && (
            <StepImport onImported={handleImported} />
          )}
          {wizard.step === 'basics' && (
            <StepBasics
              commonInfo={commonInfo}
              data={data}
              updatePersonalInfo={editing.updatePersonalInfo}
            />
          )}
          {wizard.step === 'experience' && (
            <StepExperience
              data={data}
              updateExperience={editing.updateExperience}
              addExperience={editing.addExperience}
              removeExperience={editing.removeExperience}
            />
          )}
          {wizard.step === 'education' && (
            <StepEducation
              data={data}
              updateEducation={editing.updateEducation}
              addEducation={editing.addEducation}
              removeEducation={editing.removeEducation}
            />
          )}
          {wizard.step === 'skills' && (
            <StepSkills data={data} setData={setData} removeSkill={editing.removeSkill} />
          )}
          {wizard.step === 'more' && (
            <StepMore
              data={data}
              setData={setData}
              removeLanguage={editing.removeLanguage}
              removeHobby={editing.removeHobby}
            />
          )}
          {wizard.step === 'template' && (
            <StepTemplate activeLayout={activeLayout} onLayoutChange={handleLayoutChange} />
          )}
          {wizard.step === 'prescoreats' && (
            <StepPreScoreAts data={data} />
          )}
          {wizard.step === 'reformatage' && (
            <StepReformatage data={data} setData={setData} />
          )}
          {wizard.step === 'done' && (
            <StepDone data={data} commonInfo={commonInfo} onFinish={handleFinish} />
          )}
        </div>
      </main>

      {/* Footer navigation */}
      {!hideFooter && (
        <footer className="shrink-0 border-t border-white/10 px-6 py-4 bg-black/20">
          <div className="max-w-3xl mx-auto flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={wizard.goPrev}
              disabled={isFirstStep}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white/70 hover:text-white hover:bg-white/5 transition-colors disabled:opacity-30 disabled:pointer-events-none"
            >
              <ArrowLeft className="size-4" />
              Précédent
            </button>

            <div className="flex items-center gap-2">
              {isSkippable && (
                <button
                  type="button"
                  onClick={wizard.goNext}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-white/50 hover:text-white/70 transition-colors"
                >
                  Passer →
                </button>
              )}
              <button
                type="button"
                onClick={wizard.goNext}
                className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-bold bg-blue-600 text-white hover:bg-blue-500 shadow-lg shadow-blue-600/20 transition-all"
              >
                Suivant
                <ArrowRight className="size-4" />
              </button>
            </div>
          </div>
        </footer>
      )}
    </div>
  );
}

const STEP_LABELS: Record<WizardStepKey, string> = {
  welcome:     'Démarrer',
  freetext:    'Présentation',
  import:      'Importer',
  basics:      'Moi',
  experience:  'Expérience',
  education:   'Formation',
  skills:      'Compétences',
  more:        'Plus',
  template:    'Modèle',
  prescoreats: 'Score ATS',
  reformatage: 'Reformatage',
  done:        'Prêt',
};
